import { useMemo } from 'react';
import { from, interval, Observable, of, Subject } from 'rxjs';
import { delay, filter, switchMap, takeUntil } from 'rxjs/operators';
import { checkOutdated } from '../../api/Drafts';
import { useAuthenticateApiCall } from '../hooks/useAuthenticateApiCall';
import { useSubscription } from '../hooks/useSubscription';
import { BusEvent } from './EditorContext';
import { DocumentState } from './useDocumentState';
import { EditorValueState } from './useEditorMachine';
import { DELAYS } from './useInputHandling';

const useOutdatedPolling = (
  bus$: Subject<BusEvent>,
  dispatch: (e: BusEvent) => void,
  state$: Observable<EditorValueState>,
  id: string,
  document: DocumentState
) => {
  const authedCheckOutdated = useAuthenticateApiCall(checkOutdated);

  const activity$ = useMemo(() => bus$.pipe(filter((e) => e.type === 'Editor')), [bus$]);
  const idleTrigger$ = useMemo(() => state$.pipe(filter((s) => s.value === 'saved')), [state$]);

  useSubscription(
    () =>
      idleTrigger$
        .pipe(
          switchMap(() =>
            of(undefined).pipe(
              delay(DELAYS.IDLE_TIME),
              takeUntil(activity$),
              switchMap(() =>
                interval(DELAYS.POLLING_INTERVAL).pipe(
                  switchMap(() => from(authedCheckOutdated(id, document?.title, document?.body)))
                )
              )
            )
          )
        )
        .subscribe(({ outdated }) => outdated && dispatch({ type: 'INITIALISE' })),
    [idleTrigger$, activity$, document, dispatch, authedCheckOutdated, id]
  );
};

export default useOutdatedPolling;
